/**
 * Form Builder Page
 * 
 * The main form editing interface with three-panel layout:
 * - Left: Steps panel (drag-and-drop reordering)
 * - Center: Canvas with fields for selected step/variant
 * - Right: Properties panel for selected item
 */

import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Save,
  Eye,
  Upload,
  MoreVertical,
  Check,
  Loader2,
  AlertCircle,
} from 'lucide-react';
import { FormBuilderProvider, useFormBuilder } from '../../contexts';
import { StepsPanel } from '../../components/form-builder/StepsPanel';
import { CanvasArea } from '../../components/form-builder/CanvasArea';
import { PropertiesPanel } from '../../components/form-builder/PropertiesPanel';
import { FieldTypeSelector } from '../../components/form-builder/FieldTypeSelector';
import { PublishModal } from '../../components/form-builder/PublishModal';
import { PreviewModal } from '../../components/form-builder/PreviewModal';
import './FormBuilderPage.css';

// =============================================================================
// Header Component
// =============================================================================

function FormBuilderHeader() {
  const { state, saveForm, togglePreview, togglePublishModal } = useFormBuilder();
  const { form, saveStatus, isDirty } = state;

  const handleSave = async () => {
    await saveForm();
  };

  const handlePreview = () => {
    togglePreview(true);
  };

  const handlePublish = () => {
    togglePublishModal(true);
  };

  const getSaveStatusIcon = () => {
    switch (saveStatus) {
      case 'saving':
        return <Loader2 size={14} className="spin" />;
      case 'saved':
        return <Check size={14} />;
      case 'error':
        return <AlertCircle size={14} />;
      default:
        return null;
    }
  };

  const getSaveStatusText = () => {
    switch (saveStatus) {
      case 'saving':
        return 'Saving...';
      case 'saved':
        return 'Saved';
      case 'error':
        return 'Error saving';
      default:
        return isDirty ? 'Unsaved changes' : '';
    }
  };

  return (
    <header className="form-builder-header">
      <div className="header-left">
        <Link to="/dashboard/forms" className="back-link">
          <ArrowLeft size={20} />
        </Link>
        <div className="form-info">
          <h1 className="form-name">{form?.name || 'Loading...'}</h1>
          <div className={`save-status ${saveStatus}`}>
            {getSaveStatusIcon()}
            <span>{getSaveStatusText()}</span>
          </div>
        </div>
      </div>

      <div className="header-actions">
        <button
          className="btn btn-ghost"
          onClick={handleSave}
          disabled={!isDirty || saveStatus === 'saving'}
        >
          <Save size={18} />
          <span>Save</span>
        </button>
        <button className="btn btn-ghost" onClick={handlePreview}>
          <Eye size={18} />
          <span>Preview</span>
        </button>
        <button className="btn btn-primary" onClick={handlePublish}>
          <Upload size={18} />
          <span>Publish</span>
        </button>
        <button className="btn btn-icon btn-ghost">
          <MoreVertical size={18} />
        </button>
      </div>
    </header>
  );
}

// =============================================================================
// Main Content
// =============================================================================

function FormBuilderContent() {
  const { state } = useFormBuilder();
  const { isLoading, loadError, form } = state;

  if (isLoading) {
    return (
      <div className="form-builder-loading">
        <Loader2 className="spin" size={48} />
        <p>Loading form...</p>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="form-builder-error">
        <AlertCircle size={48} />
        <h2>Failed to load form</h2>
        <p>{loadError}</p>
        <Link to="/dashboard/forms" className="btn btn-primary">
          Back to Forms
        </Link>
      </div>
    );
  }

  if (!form) {
    return (
      <div className="form-builder-error">
        <AlertCircle size={48} />
        <h2>Form not found</h2>
        <p>The form you're looking for doesn't exist.</p>
        <Link to="/dashboard/forms" className="btn btn-primary">
          Back to Forms
        </Link>
      </div>
    );
  }

  return (
    <div className="form-builder-layout">
      <StepsPanel />
      <CanvasArea />
      <PropertiesPanel />
    </div>
  );
}

// =============================================================================
// Modals
// =============================================================================

function FormBuilderModals() {
  const { state, toggleFieldTypeSelector, togglePublishModal, togglePreview } = useFormBuilder();
  const { isFieldTypeSelectorOpen, isPublishModalOpen, isPreviewOpen } = state;

  return (
    <>
      {isFieldTypeSelectorOpen && (
        <FieldTypeSelector onClose={() => toggleFieldTypeSelector(false)} />
      )}
      {isPublishModalOpen && (
        <PublishModal onClose={() => togglePublishModal(false)} />
      )}
      {isPreviewOpen && (
        <PreviewModal onClose={() => togglePreview(false)} />
      )}
    </>
  );
}

// =============================================================================
// Main Page Component
// =============================================================================

function FormBuilderPageInner() {
  return (
    <div className="form-builder-page">
      <FormBuilderHeader />
      <FormBuilderContent />
      <FormBuilderModals />
    </div>
  );
}

export function FormBuilderPage() {
  const { formId } = useParams<{ formId: string }>();
  const navigate = useNavigate();

  if (!formId) {
    navigate('/dashboard/forms');
    return null;
  }

  return (
    <FormBuilderProvider formId={formId}>
      <FormBuilderPageInner />
    </FormBuilderProvider>
  );
}

export default FormBuilderPage;
